import safeHtml from 'Helpers/safeHtml'
import { notifyParent } from 'mailup-react-helpers'
import {
  ACTION_BUTTON_TYPE_EDIT,
  ButtonAction,
  Flex,
  FlexItem,
  Grid,
  GRID_VARIANT_COLLAPSED,
  Link,
  LINK_VARIANT_HIGHLIGHT,
  Panel,
  SPACING_24,
  SpacingVertical,
  Text,
  TEXT_VARIANT_BOLD,
  TEXT_VARIANT_LIGHT,
  TEXT_VARIANT_SMALL_LIGHT,
  TEXT_VARIANT_STANDARD,
  Title,
  TITLE_VARIANT_M,
  TITLE_VARIANT_S,
  TruncateAndTooltip
} from 'mailup-web-components'
import { editCallback } from 'Pages/Workflow/recoil/callbacks/callbacks'
import {
  workflowCommonDataState,
  workflowDetails,
  workflowEmailMessageSubjectSelector,
  workflowSmsMessageSubjectSelector
} from 'Pages/Workflow/recoil/state/data'
import { workflowStepConditionDescriptionState } from 'Pages/Workflow/recoil/state/step5'
import PropTypes from 'prop-types'

import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import {
  StartEndLabel,
  StepDescription,
  StepType,
  StepTypeLabel,
  Timeline,
  TimelineEnd,
  TimelineStart,
  WorkflowRight
} from './BoxWorkflow.styles'

const EmailItem = ({ idMessage }) => {
  const intl = useIntl()

  const subject = useRecoilValue(workflowEmailMessageSubjectSelector(idMessage))

  const handleClick = () => {
    notifyParent('openPreviewMainFrame', [idMessage])
  }

  return (
    <Grid cols={12} variant={GRID_VARIANT_COLLAPSED}>
      <StepType span={3}>
        <StepTypeLabel variant={TITLE_VARIANT_S}>
          {intl.formatMessage({
            id: 'Load.AutomationWorkflow.DragElementMessage'
          })}
        </StepTypeLabel>
      </StepType>
      <StepDescription span={9}>
        <p>
          <Link variant={LINK_VARIANT_HIGHLIGHT} onClick={handleClick}>
            <TruncateAndTooltip text={subject} />
          </Link>

          <Text variant={TEXT_VARIANT_SMALL_LIGHT}>
            {intl.formatMessage({ id: 'Load.MailupCommon.Id' })} {idMessage}
          </Text>
        </p>
      </StepDescription>
    </Grid>
  )
}

EmailItem.propTypes = {
  idMessage: PropTypes.number.isRequired
}

const SmsItem = ({ idMessage }) => {
  const intl = useIntl()

  const subject = useRecoilValue(workflowSmsMessageSubjectSelector(idMessage))

  const handleClick = () => {
    notifyParent('openSMSPreview', [idMessage])
  }

  return (
    <Grid cols={12} variant={GRID_VARIANT_COLLAPSED}>
      <StepType span={3}>
        <StepTypeLabel variant={TITLE_VARIANT_S}>
          {intl.formatMessage({
            id: 'Load.AutomationWorkflow.DragElementMessageSms'
          })}
        </StepTypeLabel>
      </StepType>
      <StepDescription span={9}>
        <p>
          <Link variant={LINK_VARIANT_HIGHLIGHT} onClick={handleClick}>
            <TruncateAndTooltip text={subject} />
          </Link>

          <Text variant={TEXT_VARIANT_SMALL_LIGHT}>
            {intl.formatMessage({ id: 'Load.MailupCommon.Id' })} {idMessage}
          </Text>
        </p>
      </StepDescription>
    </Grid>
  )
}

SmsItem.propTypes = {
  idMessage: PropTypes.number.isRequired
}

const WaitItem = ({ idStep }) => {
  const intl = useIntl()

  const description = useRecoilValue(
    workflowStepConditionDescriptionState(idStep)
  )

  return (
    <Grid cols={12} variant={GRID_VARIANT_COLLAPSED}>
      <StepType span={3}>
        <StepTypeLabel variant={TITLE_VARIANT_S}>
          {intl.formatMessage({
            id: 'Load.AutomationWorkflow.DragElementWaitTypeWait'
          })}
        </StepTypeLabel>
      </StepType>
      <StepDescription span={9}>
        <Text variant={TEXT_VARIANT_BOLD}>{safeHtml(description)}</Text>
      </StepDescription>
    </Grid>
  )
}

WaitItem.propTypes = {
  idStep: PropTypes.number.isRequired
}

const ChangeEmailStatusItem = ({ EmailStatusCondition, EmailStatus }) => {
  const intl = useIntl()
  const statusLabels = {
    OptIn: intl.formatMessage({ id: 'Load.MailupCommon.Subscribed' }),
    Pending: intl.formatMessage({ id: 'Load.MailupCommon.Pending' }),
    OptOut: intl.formatMessage({ id: 'Load.MailupCommon.Unsubscribed' })
  }

  return (
    <Grid cols={12} variant={GRID_VARIANT_COLLAPSED}>
      <StepType span={3}>
        <StepTypeLabel variant={TITLE_VARIANT_S}>
          {intl.formatMessage({
            id: 'Load.AutomationWorkflow.DragElementChangeSubscriptionStatus'
          })}
        </StepTypeLabel>
      </StepType>
      <StepDescription span={9}>
        <Flex gap='8px' height='100%' alignItems='center'>
          <Text variant={TEXT_VARIANT_LIGHT}>
            {intl.formatMessage(
              {
                id: 'Load.AutomationWorkflow.ChangeSubscriptionDescription'
              },
              {
                CONDITIONSTATUS: (
                  <strong>{statusLabels[EmailStatusCondition]}</strong>
                ),
                STATUS: <strong>{statusLabels[EmailStatus]}</strong>
              }
            )}
          </Text>
        </Flex>
      </StepDescription>
    </Grid>
  )
}

ChangeEmailStatusItem.propTypes = {
  EmailStatusCondition: PropTypes.oneOf(['OptIn', 'Pending', 'OptOut']),
  EmailStatus: PropTypes.oneOf(['OptIn', 'Pending', 'OptOut'])
}

const BoxWorkflow = () => {
  const intl = useIntl()

  const { Step } = useRecoilValue(workflowDetails)
  const {
    conditionsDescription: { StartCondition, EndCondition }
  } = useRecoilValue(workflowCommonDataState)

  const edit = useRecoilCallback(editCallback, [])

  const handleEdit = () => {
    edit(3)
  }

  return (
    <Panel>
      <Flex justifyContent='space-between'>
        <Title variant={TITLE_VARIANT_M}>
          {intl.formatMessage({
            id: 'Load.AutomationWorkflow.WorkflowTitle'
          })}
        </Title>
        <ButtonAction
          type={ACTION_BUTTON_TYPE_EDIT}
          data-mu-test='edit'
          onClick={handleEdit}
        />
      </Flex>

      <SpacingVertical variant={SPACING_24} />

      <Flex alignItems='center'>
        <FlexItem flex='0 0 15%'>
          <TimelineStart>
            <StartEndLabel>
              {intl.formatMessage({
                id: 'Load.AutomationWorkflow.DragWorkflowStart'
              })}
            </StartEndLabel>
          </TimelineStart>
        </FlexItem>
        <WorkflowRight>
          <Flex
            justifyContent='flex-start'
            alignItems='center'
            padding='8px 16px'
            minHeight='58px'
          >
            <Text variant={TEXT_VARIANT_STANDARD}>
              {safeHtml(StartCondition)}
            </Text>
          </Flex>
        </WorkflowRight>
      </Flex>

      {Step.map(
        (
          {
            IdMessage,
            MessageType,
            Value,
            ValueType,
            WaitType,
            Id,
            StepType,
            EmailStatusCondition,
            EmailStatus
          },
          index
        ) => (
          <Flex alignItems='center' key={index}>
            <FlexItem flex='0 0 15%'>
              <Timeline />
            </FlexItem>
            <WorkflowRight>
              {StepType === 'Wait' && (
                <WaitItem
                  idStep={Id}
                  value={Value}
                  valueType={ValueType}
                  waitType={WaitType}
                />
              )}

              {StepType === 'SendEmail' && <EmailItem idMessage={IdMessage} />}

              {StepType === 'SendSms' && <SmsItem idMessage={IdMessage} />}

              {StepType === 'ChangeStatus' && (
                <ChangeEmailStatusItem
                  EmailStatusCondition={EmailStatusCondition}
                  EmailStatus={EmailStatus}
                />
              )}
            </WorkflowRight>
          </Flex>
        )
      )}

      <Flex alignItems='center'>
        <FlexItem flex='0 0 15%'>
          <TimelineEnd>
            <StartEndLabel>
              {intl.formatMessage({
                id: 'Load.AutomationWorkflow.DragWorkflowEnd'
              })}
            </StartEndLabel>
          </TimelineEnd>
        </FlexItem>
        <WorkflowRight>
          <Flex
            justifyContent='flex-start'
            alignItems='center'
            padding='8px 16px'
            minHeight='58px'
          >
            <Text variant={TEXT_VARIANT_STANDARD}>
              {safeHtml(EndCondition)}
            </Text>
          </Flex>
        </WorkflowRight>
      </Flex>
    </Panel>
  )
}

export default BoxWorkflow
